import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	computed,
	effect,
	inject,
	input,
	OnInit,
	output,
	signal,
	untracked,
	ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { CardList } from '../../../models/layout.model';
import { PageConfig } from '../../../models/page.model';
import { ThemeConfig } from '../../../models/theme.model';
import { ViewConfig } from '../../../models/view.model';
import { ThemeService } from '../../../services/theme.service';
import type { PageEditorPage } from '@framework/analytics/components/editors/jepto-page-editor/page-editor.model';
import { JeptoPageEditorComponent } from '@framework/analytics/components/editors/jepto-page-editor/jepto-page-editor.component';
import { JeptoPageComponent } from '../jepto-page-container/jepto-page-container.component';
import { ThemeSwitchComponent } from '../../styling/theme/jepto-theme-switch/theme-switch.component';
import type { DateRangeExpression } from '../../../models/date-range-expression.model';
/**
 * View component for displaying analytics cards and dashboards
 *
 * The View component serves as the top-level container for analytics dashboards.
 * It manages page selection, theme application, and date range handling.
 *
 * Features:
 * - Multi-page navigation with tab interface
 * - Theme control (light/dark/auto)
 * - Date range propagation from Page to App component
 * - View-only mode to prevent layout modifications
 *
 * @example
 * ```html
 * <jep-view
 *   [viewConfig]="viewConfiguration"
 *   [viewOnly]="true"
 *   [themeMode]="'light'"
 *   [themeConfig]="themeConfig">
 * </jep-view>
 * ```
 */
@Component({
	selector: 'jepto-view-container',
	templateUrl: './jepto-view-container.component.html',
	styleUrls: ['./jepto-view-container.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		JeptoPageComponent,
		MatIconModule,
		MatMenuModule,
		MatButtonModule,
		JeptoPageEditorComponent,
		ThemeSwitchComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [
		trigger('fadeIn', [
			transition(':enter', [
				style({ opacity: 0, transform: 'scale(0.95)' }),
				animate(
					'400ms ease-out',
					style({ opacity: 1, transform: 'scale(1)' })
				),
			]),
		]),
		trigger('contentFadeIn', [
			transition(':enter', [
				style({ opacity: 0, transform: 'translateY(20px)' }),
				animate(
					'500ms ease-out',
					style({ opacity: 1, transform: 'translateY(0)' })
				),
			]),
		]),
	],
})
export class JeptoViewContainerComponent implements OnInit {
	/** The view configuration containing pages, layouts and cards */
	readonly viewConfig = input<ViewConfig>();

	/**
	 * View-only mode - when true, prevents users from modifying layouts
	 * This flag is passed down to Page and Layout components
	 */
	readonly viewOnly = input<boolean>();

	/**
	 * Theme mode (light/dark/auto)
	 * - light: Force light theme
	 * - dark: Force dark theme
	 * - auto: Use system preference
	 */
	readonly themeMode = input<'light' | 'dark' | 'auto'>('auto');

	/** Theme configuration object */
	readonly themeConfig = input<ThemeConfig>();

	/** Selected page ID */
	readonly selectedPage = input<string>();

	/** Pages to be displayed in the view */
	pages = signal<PageConfig[]>([]);

	/** GridItems for the layout component */
	private gridItemsSignal = signal<CardList[]>([]);

	/** Data for each item */
	itemsData: { [key: string]: any } = {};

	/** Loading state - starts true to prevent flash of "no pages" message */
	loading = signal<boolean>(true);

	/** Error message */
	error = signal<string | null>(null);

	/** Selected page as a signal */
	selectedPageIdSignal = signal<string | null>(null);

	/** Page loading state - shown when switching between pages */
	readonly pageLoading = input<boolean>(false);

	/** Headless capture mode — hides interactive chrome for PDF/screenshot export. */
	readonly captureMode = input<boolean>(false);

	/**
	 * Report-level default date range for date-range filter cards (view settings / getView).
	 */
	readonly reportDateRangeExpression = input<
		DateRangeExpression | undefined
	>();

	/** Emitted when a date-range filter card applies a new expression. */
	readonly filterDateRangeChange = output<DateRangeExpression>();

	/** Effective theme config (explicit input, view themeId, or ThemeService). */
	readonly resolvedThemeConfig = computed(() => {
		const explicit = this.themeConfig();
		if (explicit) {
			return explicit;
		}
		const themeId = this.viewConfig()?.themeId?.trim();
		if (themeId) {
			const fromService = this.themeService.getThemeById(themeId);
			if (fromService) {
				return fromService;
			}
		}
		return this.themeService.getThemeConfig();
	});

	/** @deprecated Use resolvedThemeConfig — kept for template compatibility. */
	readonly effectiveThemeConfig = this.resolvedThemeConfig;

	/** Pages in PageEditorPage shape for jepto-page-editor (id, label, icon). */
	readonly editorPages = computed<PageEditorPage[]>(() =>
		this.pages().map(p => ({
			id: p.pageId,
			label: p.title ?? 'Page',
			icon: p.icon,
		}))
	);

	/** Track cached pages to avoid re-rendering */
	private cachedPages = new Map<string, boolean>();
	private cdr = inject(ChangeDetectorRef);
	private themeService = inject(ThemeService);

	constructor() {
		effect(() => {
			const config = this.viewConfig();
			console.log('[ViewContainer] viewConfig changed:', {
				hasConfig: !!config,
				pagesLength: config?.pages?.length ?? 0,
			});

			if (config?.pages?.length) {
				// Use untracked to prevent tracking signals read during these operations
				untracked(() => {
					this.pages.set(config.pages);
					this.loadView();
				});
			} else if (config && !config.pages?.length) {
				// Config loaded but explicitly has no pages - stop loading to show empty state
				console.log(
					'[ViewContainer] Config has no pages, showing empty state'
				);
				untracked(() => this.loading.set(false));
			}
			// If config is null/undefined, keep loading=true (waiting for data)
		});
		effect(() => {
			const selected = this.selectedPage();
			const pagesLength = this.pages().length;
			if (selected && pagesLength > 0) {
				untracked(() => this.selectPageById(selected));
			}
		});
		// Theme sync effect - must be in constructor for injection context
		effect(() => {
			this.themeService.getActiveThemeModeSignal()();
			untracked(() => this.cdr.markForCheck());
		});
		// Apply theme when the view's assigned theme is resolved in ThemeService.
		effect(() => {
			const themeId = this.viewConfig()?.themeId?.trim();
			if (!themeId) {
				return;
			}
			const config = this.resolvedThemeConfig();
			if (config.themeId !== themeId) {
				return;
			}
			this.themeService.loadThemeFromConfig(config);
		});
	}

	ngOnInit(): void {
		const config = this.viewConfig();
		if (config) {
			this.loadView();
		}
	}

	/**
	 * Get the current grid items
	 */
	get gridItems(): CardList[] {
		return this.gridItemsSignal();
	}

	/**
	 * Get the current page
	 */
	get currentPage(): PageConfig | null {
		if (!this.selectedPageIdSignal() || !this.pages()) return null;

		return this.findPageByAnyIdFormat(this.selectedPageIdSignal());
	}

	/**
	 * Get the current page ID
	 */
	get currentPageId(): string | undefined {
		return this.selectedPageIdSignal();
	}

	/**
	 * Load the view data based on viewConfig or viewId
	 */
	private loadView(): void {
		this.loading.set(true);
		this.error.set(null);

		const config = this.viewConfig();
		if (!config) {
			this.loading.set(false);
			return;
		}

		if (!config.pages || config.pages.length === 0) {
			this.loading.set(false);
			return;
		}

		this.pages.set(config.pages);

		// Auto-select the first page if no page is currently selected AND no selectedPage input is provided
		if (!this.selectedPageIdSignal() && config.pages.length > 0) {
			const selected = this.selectedPage();
			if (selected) {
				this.selectPageById(selected);
			} else {
				const firstPage = config.pages[0];
				this.selectPageById(firstPage.pageId);
			}
		}

		this.loading.set(false);
	}

	private normalizePageId(pageId: string): string {
		if (!pageId) return '';
		return pageId.startsWith('page_') ? pageId.substring(5) : pageId;
	}

	private findPageByAnyIdFormat(id: string): PageConfig | undefined {
		if (!id || !this.pages()) return undefined;

		const normalizedId = this.normalizePageId(id);
		return this.pages().find(
			p => this.normalizePageId(p.pageId) === normalizedId
		);
	}

	selectPageById(pageId: string): void {
		if (!pageId) {
			return;
		}

		const normalizedId = this.normalizePageId(pageId);
		const availablePages = this.pages();

		// If no pages are loaded yet, don't log an error - this is expected
		// during initial load when selectedPage input arrives before viewConfig
		if (!availablePages || availablePages.length === 0) {
			// Pages not yet available - the page will be selected when viewConfig loads
			return;
		}

		const page = this.findPageByAnyIdFormat(normalizedId);

		if (page) {
			this.cachedPages.set(normalizedId, true);
			this.selectedPageIdSignal.set(normalizedId);
			this.cdr.detectChanges();
		} else {
			// Only warn if pages ARE loaded but the requested page wasn't found
			// This indicates a genuine issue (e.g., invalid page ID in URL)
			console.warn(
				`[ViewContainer] Page not found: ${normalizedId}. Available pages:`,
				availablePages.map(p => p.pageId)
			);
		}
	}

	isPageCached(pageId: string): boolean {
		if (!pageId) return false;
		const normalizedId = this.normalizePageId(pageId);
		return this.cachedPages.has(normalizedId);
	}

	/**
	 * Handle date range change from page components
	 */
	onDateRangeChange(dateRange: { startDate: string; endDate: string }): void {
		console.log('[ViewComponent] Date range changed:', dateRange);
		// This could be used to propagate date range changes up to parent components
	}
}
